export const GUIDE_CONTENT = [
  {
    key: "dashboard",
    label: "Dashboard, Applications & Job Board",
    route: "/dashboard",
    overview:
      "Monitor your application workflow, review submitted jobs, and discover new opportunities.",
    groups: [
      {
        title: "Dashboard",
        items: [
          "Review headline application and interview totals.",
          "Use summary cards and recent activity to spot work that needs attention.",
        ],
      },
      {
        title: "Applications",
        items: [
          "Search applications by company and job details.",
          "Filter by date, status, and personal or team scope where available.",
          "Open a saved application to review its job description, resume, cover letter, meeting information, or status.",
          "Edit interview details and download generated files from the application actions.",
        ],
      },
      {
        title: "Job Board",
        items: [
          "Search job titles and combine location, workplace, employment, experience, salary, skills, Easy Apply, sponsorship, and application-history filters.",
          "Filters, sorting, search text, and page number are saved in the URL for refreshable and shareable searches.",
          "Red company labels indicate a previous application within the selected history period.",
          "Apply opens the original listing, Copy copies its URL, and Build Resume opens an independent floating resume workflow.",
          "Refresh reloads jobs and application history; application history also updates silently every minute.",
        ],
      },
    ],
  },
  {
    key: "bid",
    label: "Build Resume",
    route: "/bid",
    overview:
      "Turn a job URL and description into an editable tailored resume.",
    groups: [
      {
        title: "Before you begin",
        items: [
          "Upload and save both DOCX templates in View Profile.",
          "Make sure employment settings match the role and experience variables in the resume template.",
        ],
      },
      {
        title: "Build and preview",
        items: [
          "Paste the original job URL and complete job description, then select Generate Resume Draft.",
          "Review company, title, job type, salary, summary, roles, experience bullets, and skills.",
          "Add, edit, or remove generated skill and experience entries before finalizing.",
        ],
      },
      {
        title: "Finalize",
        items: [
          "Finalize Resume inserts edited content into your DOCX template, creates the PDF, saves the application, charges the configured balance, and starts the download.",
        ],
      },
    ],
  },
  {
    key: "profile",
    label: "View Profile",
    route: "/profile",
    overview:
      "Manage document templates, employment generation settings, prompts, and profile presentation.",
    groups: [
      {
        title: "Document templates",
        items: [
          "Upload DOCX resume and cover-letter templates.",
          "Use Compare to inspect an uploaded template beside the supported example structure.",
          "Resume variables include role titles, dynamic role/experience sections, and skills; the cover letter uses its generated content variable.",
        ],
      },
      {
        title: "Employment settings",
        items: [
          "Add an entry for each employment section in the resume.",
          "Title prefix guides role seniority; bullet count controls generated experience length.",
          "Save after changing files, employment settings, years of experience, or prompts.",
        ],
      },
    ],
  },
  {
    key: "analyze",
    label: "Analyze",
    route: "/analyze",
    overview:
      "Review opportunities by company and identify duplicate or scheduled activity.",
    groups: [
      {
        title: "Search and filters",
        items: [
          "Choose the date period and country or job type used for analysis.",
          "Filter scheduled versus unscheduled work and already-bidded versus new companies.",
          "Search company or job details, then use pagination to review grouped results.",
        ],
      },
    ],
  },
  {
    key: "calls",
    label: "Interviews",
    route: "/calls",
    overview: "Track your interviews, meeting details, callers, and outcomes.",
    groups: [
      {
        title: "Controls",
        items: [
          "Search and filter interview records by the available date and status controls.",
          "Switch supported list, calendar, or horizon views to inspect the same schedule differently.",
          "Open records to review job details, meeting links, notes, and generated documents.",
        ],
      },
    ],
  },
  {
    key: "teamCalls",
    label: "Team Interviews",
    route: "/team-interviews",
    overview: "Review interview activity across authorized teammates.",
    groups: [
      {
        title: "Team workflow",
        items: [
          "Use filters and view controls to find calls by teammate, date, and status.",
          "Open an interview for its job context, schedule, caller, and meeting information.",
        ],
      },
    ],
  },
  {
    key: "calendar",
    label: "Calendar",
    route: "/calendar",
    overview: "See personal interview events in a calendar timeline.",
    groups: [
      {
        title: "Calendar controls",
        items: [
          "Move between date periods and open events for full interview details.",
          "Calendar times use the timezone configured in Account Settings.",
        ],
      },
    ],
  },
  {
    key: "teamCalendar",
    label: "Team Calendar",
    route: "/team-calendar",
    overview: "See authorized team interview events together.",
    groups: [
      {
        title: "Calendar controls",
        items: [
          "Navigate the date range and select an event to inspect its teammate, job, status, and meeting details.",
        ],
      },
    ],
  },
  {
    key: "statistic",
    label: "Team Statistic",
    route: "/statistic",
    overview: "Compare team bid and interview performance.",
    groups: [
      {
        title: "Reports",
        items: [
          "Choose a week and switch between Bid and Interview metrics.",
          "Apply the filter to refresh charts and teammate totals.",
        ],
      },
    ],
  },
  {
    key: "personalStatistic",
    label: "Personal Statistic",
    route: "/personal-statistic",
    overview: "Review your personal application and interview performance.",
    groups: [
      {
        title: "Reports",
        items: [
          "Select a reporting period to update your totals and performance charts.",
        ],
      },
    ],
  },
  {
    key: "candidates",
    label: "Candidates",
    route: "/candidate",
    overview: "Create, search, review, and maintain candidate records.",
    groups: [
      {
        title: "Candidate controls",
        items: [
          "Search candidate information and use row actions to view, edit, or remove records according to your access.",
          "Upload or download candidate documents where those actions are available.",
        ],
      },
    ],
  },
  {
    key: "jobPosting",
    label: "Job Posting",
    route: "/job-posting",
    overview: "Create and manage positions shown to candidates.",
    groups: [
      {
        title: "Posting controls",
        items: [
          "Search across job details, create a new post, and edit existing position content.",
          "Use publishing controls to manage whether a position is available.",
        ],
      },
    ],
  },
  {
    key: "teamChat",
    label: "Team Chat",
    route: "/team-chat",
    overview: "Exchange real-time messages and files with teammates.",
    groups: [
      {
        title: "Messaging",
        items: [
          "Choose a channel or teammate, search conversation content, and send messages or supported attachments.",
          "Unread badges identify conversations with new activity.",
        ],
      },
    ],
  },
  {
    key: "teamMates",
    label: "Team",
    route: "/team-mate",
    overview:
      "Find teammates and review their workspace identity and availability.",
    groups: [
      {
        title: "Directory",
        items: [
          "Search teammates by name or country and use their available contact or profile actions.",
        ],
      },
    ],
  },
  {
    key: "accountSettings",
    label: "Account Settings",
    route: "/account-settings",
    overview: "Update personal account information and regional preferences.",
    groups: [
      {
        title: "Settings",
        items: [
          "Edit your name and other permitted account fields.",
          "Set country and timezone carefully because reports, interviews, and calendars use these values.",
        ],
      },
    ],
  },
  {
    key: "billing",
    label: "Billing",
    route: "/billing",
    overview:
      "Review available balance, AI usage charges, and transaction history.",
    groups: [
      {
        title: "Billing controls",
        items: [
          "Use category filters to inspect transactions and understand resume or cover-letter charges.",
          "Pricing shows the current cost of supported generation actions.",
        ],
      },
    ],
  },
  {
    key: "dailyReport",
    label: "Daily Report",
    route: "/daily-report",
    overview: "Record and review daily work updates.",
    groups: [
      {
        title: "Reports",
        items: [
          "Choose the relevant day or week, enter the requested update details, and save.",
          "Authorized views can review teammate reports.",
        ],
      },
    ],
  },
  {
    key: "emailTemplate",
    label: "Email Templates",
    route: "/email-template",
    overview: "Maintain reusable application and interview email content.",
    groups: [
      {
        title: "Template editor",
        items: [
          "Edit subjects and HTML content, insert supported variables such as job title and company, then save before use.",
        ],
      },
    ],
  },
  {
    key: "prompt",
    label: "AI Prompt",
    route: "/prompt",
    overview:
      "Manage shared AI instructions used by authorized generation features.",
    groups: [
      {
        title: "Prompt controls",
        items: [
          "Review existing instructions, edit carefully, and save to affect future AI output.",
          "Prompts influence generated content but do not replace the final human review step.",
        ],
      },
    ],
  },
  {
    key: "manageProfiles",
    label: "Manage Profiles",
    route: "/manage-profiles",
    overview: "Maintain team profiles and individual access overrides.",
    groups: [
      {
        title: "Administration",
        items: [
          "Search users, open a profile, update allowed account fields, and assign page permissions where authorized.",
        ],
      },
    ],
  },
  {
    key: "screenshots",
    label: "Screenshots",
    route: "/screenshots",
    overview: "Review authorized teammate activity captures.",
    groups: [
      {
        title: "Monitoring",
        items: [
          "Select a teammate, inspect available captures, and use filters or window controls to focus the activity view.",
        ],
      },
    ],
  },
  {
    key: "jobBoardSettings",
    label: "Job Board Settings",
    route: "/job-board-settings",
    overview:
      "Configure and validate the organization’s external Job Board connection.",
    groups: [
      {
        title: "Administration",
        items: [
          "Set the Board API, fallback profile, and access token.",
          "Decode token metadata and check balance before saving connection changes.",
        ],
      },
    ],
  },
];

export const GUIDE_CONTROL_DETAILS = {
  dashboard: {
    buttons: [
      "Application row actions — open job details, copy information, download the job description or generated documents, edit interview data, or delete an application when permitted.",
      "Job Board Apply — resolves and opens the original job listing after its details finish loading.",
      "Job Board Copy — copies the resolved original job URL; the check icon confirms success.",
      "Job Board Build Resume — opens a company-specific floating builder and generates a draft automatically. It is disabled for previously applied companies, duplicate open company windows, incomplete details, or jobs with no responsibilities, requirements, and benefits.",
      "Job Board Refresh — reloads job results and application history without clearing selected filters.",
      "Clear all — resets Job Board filters to defaults, including the 20-day application-history period.",
      "Newest / Recommended — changes Job Board result ordering and returns to page one.",
      "Finalize Resume — saves the application, creates the final files, charges the configured balance, and downloads the PDF.",
      "Floating window minus, plus, and close — minimize, restore, or discard an in-progress Job Board resume window.",
    ],
    filters: [
      "Application search — finds submitted applications using company and available job information.",
      "Application date range — limits records and statistics to the selected dates; All Applications removes the normal date restriction where available.",
      "Application status — narrows results by waiting, interview stage, final, or failed status.",
      "Job Board search — searches job titles or terms and updates after a short typing delay.",
      "Date Posted — selects 24 hours, 3 days, 7 days, 1 month, or 3 months.",
      "Location — selects one or more countries.",
      "Workplace — selects onsite, hybrid, remote, or unspecified work.",
      "Employment Type — selects full-time, part-time, contractor, temporary, intern, volunteer, per-diem, or other.",
      "Experience Level — selects entry, mid, senior, executive, internship, associate, director, or unspecified levels.",
      "Salary — sets minimum, maximum, currency, and yearly, monthly, or hourly period.",
      "Skills — selects known skills or accepts custom tags.",
      "Easy Apply and H1B Sponsorship — toggle those requirements independently.",
      "Application History — controls how many previous days are checked for red already-applied company highlighting; it does not change job results.",
      "Pagination — loads another result page while retaining filters in the URL.",
    ],
  },
  bid: {
    buttons: [
      "Generate Resume Draft — validates the job URL, job description, and uploaded resume template, then opens an editable AI draft without saving an application.",
      "Clear — empties the entered job URL and description.",
      "Add skill / Add entry — appends an editable skill or experience field.",
      "Remove beside a skill or experience — removes only that editable entry from the draft.",
      "Finalize Resume — inserts the reviewed draft into the DOCX template, creates files, saves the application, updates balance, and downloads the PDF.",
    ],
    filters: [
      "Job URL — must contain the source listing URL.",
      "Job Description — should contain the full role content because it supplies the AI tailoring context.",
      "Preview fields — company, titles, job type, salary, summary, roles, experiences, and skills can all be corrected before finalizing.",
    ],
  },
  profile: {
    buttons: [
      "Edit — opens Account Settings for read-only identity fields.",
      "DOCX upload area — click or drag a resume or cover-letter file; validation errors appear beside the template label.",
      "Compare resume / Compare cover letter — opens the uploaded DOCX beside supported example variables and formatting guidance.",
      "Template tour — replays the document-template and employment-settings walkthrough.",
      "Add employment — creates another role/experience section configuration.",
      "Remove employment — deletes an employment configuration; at least two must remain.",
      "Save / Save Profile Content — validates both templates, employment settings, years of experience, and required prompts before storing changes.",
    ],
    filters: [
      "Title prefix — selects the seniority wording that guides each generated employment role.",
      "Bullet count — sets how many tailored experience bullets AI generates for that employment.",
      "Years of experience — supplies overall seniority context and accepts a whole number from 0 to 100.",
      "Bid and Call prompts — customize personal AI instructions when the user has access to special AI actions.",
    ],
  },
  analyze: {
    buttons: [
      "Apply/Search — refreshes grouped opportunity results using the selected analysis controls.",
      "Company or job actions — open source details, copy available content, or continue into an authorized application workflow.",
      "Pagination — moves through grouped companies without losing applied filters.",
    ],
    filters: [
      "Period — sets the start and end dates used by analysis.",
      "Country / Job Type — limits results to the relevant regional team or job market.",
      "Scheduled — shows all, scheduled, or unscheduled opportunities.",
      "Bid status — shows all, already-bidded, or not-yet-bidded companies.",
      "Search — matches company names, job URLs, and role titles.",
    ],
  },
  calls: {
    buttons: [
      "View controls — switch between supported table, calendar, and horizon presentations.",
      "Interview row/card — opens the selected interview and its job context.",
      "Copy meeting link or job URL — copies the corresponding value.",
      "Download — downloads available job descriptions, resumes, or cover letters.",
      "Edit/Save — updates authorized caller, status, schedule, meeting link, client address, and notes.",
    ],
    filters: [
      "Search — matches available interview, company, caller, or job information.",
      "Date controls — limit interviews to the selected period.",
      "Status and caller controls — narrow results to an interview stage or assigned caller where available.",
    ],
  },
  teamCalls: {
    buttons: [
      "View controls — switch the team interview presentation.",
      "Interview item — opens full schedule, teammate, meeting, and job details.",
      "Copy and download actions — copy URLs or retrieve available documents.",
    ],
    filters: [
      "Date range — limits team interviews to the selected period.",
      "Teammate, caller, status, and search controls — narrow the shared interview list where available.",
    ],
  },
  calendar: {
    buttons: [
      "Previous / Next / Today — move the visible calendar period.",
      "Calendar event — opens interview details and available job or meeting actions.",
      "Copy / Download — copies URLs or downloads available documents from the event details.",
    ],
    filters: [
      "Calendar view and date controls — choose the displayed period; all times follow Account Settings timezone.",
    ],
  },
  teamCalendar: {
    buttons: [
      "Previous / Next / Today — navigate the shared calendar.",
      "Team event — opens teammate, job, interview-stage, and meeting information.",
    ],
    filters: [
      "Calendar period — changes the visible team schedule.",
      "Available teammate or status controls — narrow shared events where provided.",
    ],
  },
  statistic: {
    buttons: [
      "Apply Filter — recalculates team charts and totals for the chosen controls.",
    ],
    filters: [
      "Week — selects the reporting week.",
      "Bid / Interview — switches the performance dataset shown in charts.",
    ],
  },
  personalStatistic: {
    buttons: [
      "Apply/Refresh — reloads personal performance charts for the selected period.",
    ],
    filters: [
      "Date or week control — changes the period used for personal bid and interview totals.",
    ],
  },
  interview: {
    buttons: [
      "Start/Continue — begins or resumes the assisted interview session.",
      "Microphone control — starts or stops voice capture when browser permission is available.",
      "Submit/Next — sends the current response and advances the interview flow.",
      "Finish — completes the active interview session.",
    ],
    filters: [
      "Interview prompt and response fields — show the active question, transcript, and editable response information available in the session.",
    ],
  },
  candidates: {
    buttons: [
      "New/Add Candidate — opens the candidate form.",
      "View — opens candidate information.",
      "Edit/Save — changes the selected candidate.",
      "Delete — permanently removes the selected candidate after confirmation.",
      "Upload/Download — attaches or retrieves supported candidate documents where available.",
    ],
    filters: [
      "Search — matches candidate identity and available profile fields.",
      "Table pagination — moves through candidate results.",
    ],
  },
  jobPosting: {
    buttons: [
      "New job post — opens a blank position form.",
      "Edit/Save — changes job details or creates the new post.",
      "Delete — removes a job after confirmation.",
      "Publishing status control — enables or disables public availability.",
    ],
    filters: [
      "Search jobs — matches text across job details.",
      "Pagination — moves through managed job posts.",
    ],
  },
  teamChat: {
    buttons: [
      "New message / Send — sends typed content to the selected conversation.",
      "Attachment — selects supported files for upload.",
      "Channel or member — opens that conversation.",
      "Search — finds conversations or message content supported by the current view.",
      "Channel management actions — create, edit, add members, or leave when permitted.",
    ],
    filters: [
      "Conversation search — filters channels or teammates.",
      "Message search — locates matching content in the selected conversation where available.",
    ],
  },
  teamMates: {
    buttons: [
      "Teammate card — opens or exposes the available profile/contact action.",
    ],
    filters: [
      "Search — matches teammate name or country.",
      "Country grouping — helps identify the teammate’s job market.",
    ],
  },
  accountSettings: {
    buttons: ["Save — validates and stores editable account preferences."],
    filters: [
      "Country — sets the user’s job-market country.",
      "Timezone — controls displayed calendar, interview, report, and header times.",
      "Other identity fields — update the editable personal values shown in Profile.",
    ],
  },
  billing: {
    buttons: [
      "Pricing or purchase actions — open the available balance/pricing workflow where configured.",
    ],
    filters: [
      "Transaction category — filters charges by usage type.",
      "History pagination — moves through balance transactions.",
    ],
  },
  dailyReport: {
    buttons: [
      "Add/Save Report — validates and stores the daily update.",
      "Edit/Delete — changes or removes an authorized report.",
      "Week navigation — moves between report periods.",
    ],
    filters: [
      "Week/date — limits reports to the selected period.",
      "Teammate or status controls — narrow reports where role access provides them.",
    ],
  },
  emailTemplate: {
    buttons: [
      "Save — stores the edited subject and HTML template.",
      "Variable references — show supported placeholders that can be inserted into content.",
    ],
    filters: [
      "Template selector — chooses the email type to edit where multiple templates are available.",
      "Subject and content fields — define the reusable message and supported variables.",
    ],
  },
  prompt: {
    buttons: [
      "Save/Update Prompt — validates and stores shared AI instructions.",
    ],
    filters: [
      "Prompt editor — contains the instruction text used by future authorized AI requests.",
    ],
  },
  manageProfiles: {
    buttons: [
      "User card/Edit — opens the selected profile for management.",
      "Save — stores profile and permission changes.",
      "Permission toggles — grant or remove individual page access overrides.",
    ],
    filters: [
      "Search — matches user name, email, role, country, and supported profile fields.",
      "Role/page permissions — control what that user can open; the Guide updates from the effective result.",
    ],
  },
  screenshots: {
    buttons: [
      "Teammate selector — opens that user’s activity captures.",
      "Window controls — focus or close available activity windows.",
      "Refresh — retrieves current capture information where available.",
    ],
    filters: [
      "User/activity controls — limit the view to the selected teammate or capture type.",
    ],
  },
  jobBoardSettings: {
    buttons: [
      "Decode Token — displays creation, expiry, and validity information without saving settings.",
      "Check Balance — requests the external Job Board balance.",
      "Save Settings — validates and stores Board API, fallback profile ID, and bearer token.",
    ],
    filters: [
      "Board API — sets the external service base address.",
      "Fallback Profile ID — supplies the profile used when a user-specific value is unavailable.",
      "Bearer Token — authorizes protected external Job Board requests.",
    ],
  },
};
